import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDataTable from 'react-data-table-component';
import { IconCar, IconEye } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import ActionDropdown from '../../components/common/ActionDropdown';
import { TableLoader } from '../../components/common/Loader';
import { useVehicleRequests, useVehicleRequestSummary } from '../../hooks/useVehicleRequests';
import { statusLabel, statusBadgeClass, formatDate, formatTime, dataTableStyles, isForbidden, VEH_DISPATCH_PERMISSIONS } from './utils';

// awaiting_me=true covers named reviewer, named approver, and (for dispatch) approved-unassigned.
export default function VehicleApprovals() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const [filterText, setFilterText] = useState('');
    const canDispatch = hasAnyPermission(VEH_DISPATCH_PERMISSIONS);

    const { data: requests = [], isLoading, error } = useVehicleRequests(orgId, { awaiting_me: true });
    const { data: summary } = useVehicleRequestSummary(orgId);

    const columns = [
        {
            name: 'Reference',
            selector: row => row.reference_no,
            sortable: true,
            cell: row => (
                <div>
                    <p className="font-medium text-sm">{row.reference_no || '-'}</p>
                    <p className="text-xs text-text-secondary mt-1">{row.requester_name || ''}</p>
                </div>
            ),
            minWidth: '160px',
        },
        {
            name: 'Purpose',
            selector: row => row.purpose_of_trip,
            cell: row => <span className="text-sm">{row.purpose_of_trip || '-'}</span>,
            minWidth: '240px',
        },
        {
            name: 'Departure',
            selector: row => row.departure_date,
            sortable: true,
            cell: row => (
                <div className="text-sm">
                    <p>{formatDate(row.departure_date)}</p>
                    {row.departure_time && <p className="text-xs text-text-secondary mt-1">{formatTime(row.departure_time)}</p>}
                </div>
            ),
            width: '140px',
        },
        {
            name: 'Waiting On',
            selector: row => row.status,
            cell: row => (
                <span className={`px-8 py-3 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass(row.status)}`}>
                    {statusLabel(row)}
                </span>
            ),
            width: '170px',
        },
        {
            name: 'Actions',
            // List payload is trimmed (no ids for pickers) — this just opens the detail page.
            cell: row => (
                <ActionDropdown
                    menuItems={[
                        {
                            label: 'View Details',
                            icon: IconEye,
                            onClick: () => navigate(`/vehicle-requests/${row.id}`),
                            description: row.status === 'approved' ? 'Assign a vehicle' : 'Review, approve or reject',
                        },
                    ]}
                />
            ),
            width: '90px',
            center: true,
        },
    ];

    const filteredItems = useMemo(() => {
        if (!filterText) return requests;
        const q = filterText.toLowerCase();
        return requests.filter(r =>
            (r.reference_no || '').toLowerCase().includes(q) ||
            (r.purpose_of_trip || '').toLowerCase().includes(q) ||
            (r.requester_name || '').toLowerCase().includes(q)
        );
    }, [requests, filterText]);

    const subHeaderComponent = useMemo(() => (
        <div className="flex gap-5 mb-10 w-full justify-end">
            <input
                type="text"
                placeholder="Search..."
                className="bg-bg-light border border-border w-full max-w-[250px] py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText]);

    if (isForbidden(error)) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Vehicle Approvals" path="Vehicle / Approvals" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    You do not have access to vehicle approvals.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Vehicle Approvals" path="Vehicle / Approvals" />

            {canDispatch && summary?.approved_unassigned > 0 && (
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 flex items-center gap-10">
                    <IconCar size={20} className="text-primary" />
                    <p className="text-sm">
                        <span className="font-semibold">{summary.approved_unassigned}</span> approved
                        {summary.approved_unassigned === 1 ? ' request is' : ' requests are'} waiting for a vehicle.
                    </p>
                </div>
            )}

            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-center mb-20 flex-wrap gap-10">
                    <h2 className="text-lg font-bold">Awaiting My Action</h2>
                    {summary?.awaiting_my_action > 0 && (
                        <span className="px-10 py-3 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/30">
                            {summary.awaiting_my_action} pending
                        </span>
                    )}
                </div>

                {isLoading ? (
                    <TableLoader />
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        pointerOnHover
                        persistTableHead
                        onRowClicked={(row) => navigate(`/vehicle-requests/${row.id}`)}
                        noDataComponent={
                            <div className="py-40 text-center text-text-secondary text-sm">
                                Nothing is waiting on you right now.
                            </div>
                        }
                        customStyles={dataTableStyles}
                    />
                )}
            </div>
        </div>
    );
}
